import queryClient from "api/queryClient.ts";
import { useUpdateIncomingPostbackMutation } from "api/TenantSlug/IncomingPostback/hooks.ts";
import { ErrorsChartSection } from "components/Forms/ConnectionCreatorGeneralFormSections/ErrorsChartSection.tsx";
import { IncomingPostbackGeneralFormSection } from "components/Forms/ConnectionCreatorGeneralFormSections/IncomingPostbackGeneralFormSection/IncomingPostbackGeneralFormSection.tsx";
import { InformationSection } from "components/Forms/ConnectionCreatorGeneralFormSections/InformationSection.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { MULTISELECT_ALL_VALUE } from "localConstants.ts";
import { FC, useEffect } from "react";
import { toast } from "react-toastify";
import { FormId } from "types/models.ts";

import { formSchema, IncomingPostbackFormData } from "./formSchema.ts";

import IncomingPostbackDataOptional = App.Data.IncomingPostbackDataOptional;

interface TrackingToolToGeneralFormProps {
    postback: IncomingPostbackDataOptional;
}

const TrackingToolFromGeneralFormContent: FC<TrackingToolToGeneralFormProps> = (props) => {
    const generalFormUtils = useGeneralForm<IncomingPostbackFormData>();
    const { reset } = generalFormUtils;

    // Reset form when postback changes (after refetch)
    useEffect(() => {
        if (props.postback) {
            reset(transformPostbackToFormData(props.postback));
        }
    }, [props.postback, reset]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col gap-y-6">
                <IncomingPostbackGeneralFormSection incomingPostback={props.postback} />
            </div>
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground>
                    <InformationSection />
                </SectionContentWrapper>
                <SectionContentWrapper withBackground>
                    <ErrorsChartSection />
                </SectionContentWrapper>
            </div>
        </div>
    );
};

export const TrackingToolFromGeneralForm: FC<TrackingToolToGeneralFormProps> = ({ postback }) => {
    const updateIncomingPostback = useUpdateIncomingPostbackMutation(postback?.id || "");
    const { t } = usePaldockTranslation("forms.tracking-tool-from-general");

    if (!postback) return null;

    const handleMultiSelectValue = <T extends string | number>(
        arr: (T | typeof MULTISELECT_ALL_VALUE)[] = []
    ): Record<string, T> => {
        if (arr.includes(MULTISELECT_ALL_VALUE)) return {};
        return Object.fromEntries(arr.map((val) => [val, val])) as Record<string, T>;
    };

    const handleSubmit = (data: IncomingPostbackFormData) => {
        const payload = {
            id: postback.id,
            ...data,
            types: handleMultiSelectValue(data.types),
            sources: handleMultiSelectValue(data.sources),
            offer_ids: handleMultiSelectValue(data.offer_ids),
            excluded_offer_ids: handleMultiSelectValue(data.excluded_offer_ids),
            country_id: data.country_id ?? null,
            affiliate_ids: handleMultiSelectValue(data.affiliate_ids),
            advertiser_ids: handleMultiSelectValue(data.advertiser_ids),
        };

        updateIncomingPostback.mutate(payload, {
            onSuccess: () => {
                queryClient.refetchQueries({ queryKey: ["getIncomingPostback", postback.id] });
                queryClient.refetchQueries({ queryKey: ["getIncomingPostbacks"] });
                toast.success(t("messages.update_success"));
            },
            onError: (error) => {
                toast.error(`${t("messages.update_error")}: ${error.message}`);
            },
        });
    };

    const transformedDefaultValues = transformPostbackToFormData(postback);

    return (
        <GeneralFormProvider<IncomingPostbackFormData>
            schema={formSchema}
            id={FormId.TrackingToolFromGeneralForm}
            defaultValues={transformedDefaultValues}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <TrackingToolFromGeneralFormContent postback={postback} />
        </GeneralFormProvider>
    );
};

const transformPostbackToFormData = (postback: IncomingPostbackDataOptional): IncomingPostbackFormData => ({
    name: postback.name ?? "",
    description: postback.description ?? "",
    is_active: postback.is_active ?? false,
    first_sequence_step_id: postback.first_sequence_step_id ?? null,

    types: postback.types?.map((t) => (typeof t === "string" ? t : t.title)) ?? [],
    sources: postback.sources ? Object.values(postback.sources) : [],

    offer_ids: postback.offers?.length ? postback.offers.map((o) => o.id) : [MULTISELECT_ALL_VALUE],
    excluded_offer_ids: postback.excludedOffers?.map((offer) => offer.id) ?? [],
    country_id: postback.country_id ?? postback.country?.id ?? null,
    affiliate_ids: postback.affiliates?.length ? postback.affiliates.map((u) => u.id) : [MULTISELECT_ALL_VALUE],
    advertiser_ids: postback.advertisers?.length ? postback.advertisers.map((u) => u.id) : [MULTISELECT_ALL_VALUE],
});
